import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { useToast } from '../../hooks/use-toast';
import { Mail, MessageCircle, Clock, CheckCircle, Send, Trash2, RefreshCw, User, Phone } from 'lucide-react';
import ExportButtons from './ExportButtons';

const SupportTicketsManager = () => {
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [stats, setStats] = useState({ open: 0, in_progress: 0, closed: 0, total: 0 });
  const { toast } = useToast();
  
  // Ref for ticket details section
  const ticketDetailsRef = React.useRef(null);

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  useEffect(() => {
    fetchTickets();
  }, []);

  useEffect(() => {
    if (statusFilter === 'all') {
      setFilteredTickets(tickets);
    } else {
      setFilteredTickets(tickets.filter(t => t.status === statusFilter));
    }
  }, [statusFilter, tickets]);
  
  // Auto-scroll to ticket details when a ticket is selected (mobile optimization)
  useEffect(() => {
    if (selectedTicket && ticketDetailsRef.current) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        ticketDetailsRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }, 100);
    }
  }, [selectedTicket]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const url = API_BASE + '/api/admin/support/tickets';
      const response = await fetch(url, {
        headers: { 'Authorization': 'Bearer ' + token }
      });
      if (response.ok) {
        const data = await response.json();
        setTickets(data.tickets || []);
        setStats(data.stats || { open: 0, in_progress: 0, closed: 0, total: 0 });
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateTicketStatus = async (ticketId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      const url = API_BASE + '/api/admin/support/tickets/' + ticketId + '/status?status=' + newStatus;
      const response = await fetch(url, {
        method: 'PUT',
        headers: { 'Authorization': 'Bearer ' + token }
      });
      if (response.ok) {
        toast({ title: 'Success', description: 'Ticket status updated' });
        fetchTickets();
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update status', variant: 'destructive' });
    }
  };

  const deleteTicket = async (ticketId) => {
    if (!window.confirm('Are you sure you want to delete this ticket?')) return;
    try {
      const token = localStorage.getItem('token');
      const url = API_BASE + '/api/admin/support/tickets/' + ticketId;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: { 'Authorization': 'Bearer ' + token }
      });
      if (response.ok) {
        toast({ title: 'Success', description: 'Ticket deleted successfully' });
        if (selectedTicket?.id === ticketId) setSelectedTicket(null);
        fetchTickets();
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete ticket', variant: 'destructive' });
    }
  };

  const sendReply = async () => {
    if (!selectedTicket || !replyMessage.trim()) {
      toast({ title: 'Error', description: 'Please enter a reply message', variant: 'destructive' });
      return;
    }
    try {
      setSending(true);
      const token = localStorage.getItem('token');
      const url = API_BASE + '/api/admin/support/tickets/reply';
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': 'Bearer ' + token,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ticket_id: selectedTicket.id,
          reply_message: replyMessage,
          admin_email: 'welcome@trainerlist.com'
        })
      });
      if (response.ok) {
        const data = await response.json();
        toast({ 
          title: 'Success', 
          description: data.message || 'Reply sent and email delivered to user'
        });
        setReplyMessage('');
        
        // Fetch updated ticket to show new conversation
        const ticketResponse = await fetch(API_BASE + '/api/admin/support/tickets', {
          headers: { 'Authorization': 'Bearer ' + token }
        });
        
        if (ticketResponse.ok) {
          const ticketsData = await ticketResponse.json();
          const updatedTicket = ticketsData.tickets.find(t => t.id === selectedTicket.id);
          if (updatedTicket) {
            setSelectedTicket(updatedTicket);
          }
          setTickets(ticketsData.tickets);
        }
      } else {
        const error = await response.json();
        toast({ 
          title: 'Error', 
          description: error.detail || 'Failed to send reply', 
          variant: 'destructive' 
        });
      }
    } catch (error) {
      console.error('Error sending reply:', error);
      toast({ title: 'Error', description: 'Failed to send reply', variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      open: { label: 'Open', className: 'bg-blue-500 text-white' },
      in_progress: { label: 'In Progress', className: 'bg-yellow-500 text-white' },
      closed: { label: 'Closed', className: 'bg-green-500 text-white' }
    };
    const c = config[status] || config.open;
    return <Badge className={c.className}>{c.label}</Badge>;
  };

  const formatDate = (isoDate) => {
    return new Date(isoDate).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Support Tickets</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">Manage customer support requests</p>
        </div>
        <Button onClick={fetchTickets} variant="outline" className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-600 font-medium">Open</p>
              <p className="text-3xl font-bold text-blue-700">{stats.open}</p>
            </div>
            <Clock className="w-10 h-10 text-blue-400 opacity-50" />
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-yellow-600 font-medium">In Progress</p>
              <p className="text-3xl font-bold text-yellow-700">{stats.in_progress}</p>
            </div>
            <MessageCircle className="w-10 h-10 text-yellow-400 opacity-50" />
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600 font-medium">Closed</p>
              <p className="text-3xl font-bold text-green-700">{stats.closed}</p>
            </div>
            <CheckCircle className="w-10 h-10 text-green-400 opacity-50" />
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-red-200">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-red-600 font-medium">Total</p>
              <p className="text-3xl font-bold text-red-700">{stats.total}</p>
            </div>
            <Mail className="w-10 h-10 text-purple-400 opacity-50" />
          </CardContent>
        </Card>
      </div>

      <div className="flex gap-2">
        {['all', 'open', 'in_progress', 'closed'].map(status => (
          <button
            key={status}
            onClick={() => setStatusFilter(status)}
            className={'px-4 py-2 rounded-lg font-medium transition-colors ' + (
              statusFilter === status
                ? 'bg-red-600 text-white'
                : 'bg-gray-100 text-gray-700 dark:text-gray-300 hover:bg-gray-200'
            )}
          >
            {status === 'all' ? 'All' : status.split('_').map(w => w[0].toUpperCase() + w.slice(1)).join(' ')}
          </button>
        ))}
      </div>

      {/* Export Buttons */}
      <ExportButtons 
        data={filteredTickets}
        columns={[
          { header: 'ID', accessor: (row) => row.id || 'N/A' },
          { header: 'Name', accessor: (row) => row.user_name || 'N/A' },
          { header: 'Email', accessor: (row) => row.user_email || 'N/A' },
          { header: 'Phone', accessor: (row) => row.user_phone || 'N/A' },
          { header: 'Subject', accessor: (row) => row.subject || 'N/A' },
          { header: 'Status', accessor: (row) => row.status || 'N/A' },
          { header: 'Created At', accessor: (row) => row.created_at ? new Date(row.created_at).toLocaleDateString() : 'N/A' }
        ]}
        filename="support-tickets"
      />

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin w-12 h-12 border-4 border-purple-600 border dark:border-gray-700-t-transparent rounded-full mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading tickets...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold dark:text-white">{filteredTickets.length} Tickets</h3>
            {filteredTickets.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Mail className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">No tickets found</p>
                </CardContent>
              </Card>
            ) : (
              filteredTickets.map(ticket => (
                <Card
                  key={ticket.id}
                  className={'cursor-pointer transition-all hover:shadow-lg ' + (
                    selectedTicket?.id === ticket.id ? 'ring-2 ring-purple-500' : ''
                  )}
                  onClick={() => setSelectedTicket(ticket)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-semibold dark:text-white">{ticket.name}</h4>
                          {getStatusBadge(ticket.status)}
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{ticket.email}</p>
                        {ticket.phone && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1 mt-1">
                            <Phone className="w-3 h-3" />
                            {ticket.phone}
                          </p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteTicket(ticket.id);
                        }}
                        className="text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-2">{ticket.message}</p>
                    <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                      <span>{formatDate(ticket.created_at)}</span>
                      {ticket.user_role && <Badge variant="outline">{ticket.user_role}</Badge>}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          <div className="lg:sticky lg:top-6" ref={ticketDetailsRef}>
            {selectedTicket ? (
              <Card className="dark:bg-gray-800">
                <CardHeader className="bg-gradient-to-r from-red-50 to-gray-50 dark:from-gray-700 dark:to-gray-700">
                  <div className="flex items-start justify-between">
                    <CardTitle className="dark:text-white">Ticket Details</CardTitle>
                    {getStatusBadge(selectedTicket.status)}
                  </div>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                      <span className="font-semibold dark:text-white">{selectedTicket.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">{selectedTicket.email}</span>
                    </div>
                    {selectedTicket.phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                        <span className="text-sm text-gray-600 dark:text-gray-400">{selectedTicket.phone}</span>
                      </div>
                    )}
                  </div>

                  <div>
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <MessageCircle className="w-4 h-4" />
                      Conversation History
                    </h4>
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {/* Initial message from user */}
                      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border-l-4 border-blue-500 dark:border-blue-400">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-sm text-blue-900 dark:text-blue-300">{selectedTicket.name}</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">{formatDate(selectedTicket.created_at)}</span>
                        </div>
                        <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{selectedTicket.message}</p>
                        <span className="inline-block mt-2 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-1 rounded">User</span>
                      </div>

                      {/* All conversation replies */}
                      {selectedTicket.conversation && selectedTicket.conversation.map((msg, idx) => (
                        <div 
                          key={idx}
                          className={
                            msg.by_role === 'admin'
                              ? 'bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border-l-4 border-green-500 dark:border-green-400'
                              : 'bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border-l-4 border-red-500 dark:border-red-400'
                          }
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-semibold text-sm text-gray-900 dark:text-white">{msg.by_name}</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">{formatDate(msg.timestamp)}</span>
                          </div>
                          <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{msg.message}</p>
                          <span className={
                            'inline-block mt-2 text-xs px-2 py-1 rounded ' +
                            (msg.by_role === 'admin' 
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' 
                              : 'bg-purple-100 dark:bg-purple-900/30 text-red-700 dark:text-red-300')
                          }>
                            {msg.by_role === 'admin' ? 'Admin' : msg.by_role.charAt(0).toUpperCase() + msg.by_role.slice(1)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2">Change Status:</h4>
                    <div className="flex gap-2">
                      {selectedTicket.status !== 'open' && (
                        <Button size="sm" variant="outline" onClick={() => updateTicketStatus(selectedTicket.id, 'open')}>Open</Button>
                      )}
                      {selectedTicket.status !== 'in_progress' && (
                        <Button size="sm" variant="outline" onClick={() => updateTicketStatus(selectedTicket.id, 'in_progress')}>In Progress</Button>
                      )}
                      {selectedTicket.status !== 'closed' && (
                        <Button size="sm" variant="outline" onClick={() => updateTicketStatus(selectedTicket.id, 'closed')}>Close</Button>
                      )}
                    </div>
                  </div>

                  {selectedTicket.status !== 'closed' && (
                    <div>
                      <h4 className="font-semibold mb-2">Send Reply to User:</h4>
                      <Textarea
                        placeholder="Type your reply here... This will be sent to the user via email."
                        value={replyMessage}
                        onChange={(e) => setReplyMessage(e.target.value)}
                        rows={6}
                        className="mb-3"
                      />
                      <Button
                        onClick={sendReply}
                        disabled={sending || !replyMessage.trim()}
                        className="w-full bg-gradient-to-r from-red-600 to-black hover:from-red-700 hover:to-gray-900"
                      >
                        {sending ? (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Sending Email...
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-2" />
                            Send Reply & Email to User
                          </>
                        )}
                      </Button>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
                        Reply will be sent to {selectedTicket.email} and ticket will be closed
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card className="h-full">
                <CardContent className="p-12 flex flex-col items-center justify-center text-center">
                  <MessageCircle className="w-16 h-16 text-gray-300 mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">Select a ticket to view details and reply</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportTicketsManager;
